/**
 * Lot W « Champs Axonaut » (retours d'Inès du 13/10/2026) : logique pure du canal d'acquisition et de
 * l'apporteur d'un client.
 *
 * - Les fiches Axonaut portent deux champs personnalisés (`raw.custom_fields`, objet `{ "<nom>": "<valeur>" }`) :
 *   « Canal d'acquisition » et « Apporteur d'affaire ». Leurs noms sont reconnus avec tolérance (casse, accents,
 *   apostrophes typographiques, singulier/pluriel).
 * - Canal effectif d'un client = valeur Axonaut quand elle existe, sinon la saisie du Cockpit (client, puis ses
 *   devis). Les canaux ne sont plus une liste figée : une clé stable (slug) sert à regrouper, le libellé est
 *   celui tapé dans Axonaut ; `ACQUISITION_CHANNELS` ne sert plus que de libellés de repli.
 * - L'apporteur Axonaut est un nom libre (valeurs mêlées : canaux, agences, personnes) : il n'est jamais
 *   découpé, seulement rapproché des apporteurs du Cockpit par nom normalisé (nom ou alias enregistré).
 */
import type { ApporteurLinkedBy } from './schemas/acquisition.js';
import { ACQUISITION_CHANNELS, PARTNER_CHANNEL_KEY, type PartnerKind } from './schemas/data.js';

// ------------------------------------------------------------------ Normalisation

/**
 * Texte comparable : sans accents ni casse, apostrophes (droites ou typographiques) et ponctuation remplacées
 * par des espaces, « & » lu « et », espaces fusionnés.
 */
export function normalizeAttributionText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, ' et ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

/** Mot au singulier (approché) : « affaires » → « affaire », « partenaires » → « partenaire ». */
const singular = (word: string) => (word.length > 3 && word.endsWith('s') ? word.slice(0, -1) : word);

/** Forme de comparaison tolérante au pluriel (chaque mot ramené au singulier). */
export function looseKey(value: string): string {
  return normalizeAttributionText(value).split(' ').filter(Boolean).map(singular).join(' ');
}

// ------------------------------------------------------------------ Champs personnalisés Axonaut

/** Noms des champs dans Axonaut (forme de référence, pour les libellés et l'export). */
export const AXONAUT_CHANNEL_FIELD = "Canal d'acquisition";
export const AXONAUT_APPORTEUR_FIELD = "Apporteur d'affaire";

/** Rôle d'un champ personnalisé Axonaut d'après son nom, ou null s'il ne nous concerne pas. */
export function axonautFieldKind(name: string): 'channel' | 'apporteur' | null {
  const key = looseKey(name);
  if (!key) return null;
  if (/\bapporteur\b/.test(key)) return 'apporteur';
  if (/\bacquisition\b/.test(key) || /^(canal|canaux)$/.test(key)) return 'channel';
  return null;
}

/** Valeur d'un champ en texte nettoyé (bords retirés, espaces fusionnés), null si vide. */
function fieldText(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') {
    const text = value
      .replace(/[\u00a0\u202f]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return text === '' ? null : text;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return fieldText(String(value));
  if (Array.isArray(value)) {
    const parts = value.map(fieldText).filter((v): v is string => v !== null);
    return parts.length ? parts.join(', ') : null;
  }
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>;
    return fieldText(o.value ?? o.label ?? o.name ?? null);
  }
  return null;
}

/** Paires (nom, valeur) des champs personnalisés : objet `{ nom: valeur }` (format vérifié) ou liste `{ name, value }`. */
function customFieldPairs(customFields: unknown): [string, unknown][] {
  if (!customFields || typeof customFields !== 'object') return [];
  if (Array.isArray(customFields)) {
    return customFields.flatMap((f) => {
      if (!f || typeof f !== 'object') return [];
      const o = f as Record<string, unknown>;
      const name = o.name ?? o.label ?? o.title;
      return typeof name === 'string' ? [[name, o.value] as [string, unknown]] : [];
    });
  }
  return Object.entries(customFields as Record<string, unknown>);
}

export interface AxonautAttribution {
  /** « Canal d'acquisition » tel que tapé dans Axonaut (nettoyé), null si vide. */
  channel: string | null;
  /** « Apporteur d'affaire » tel que tapé dans Axonaut (nettoyé), null si vide. */
  apporteur: string | null;
}

/**
 * Les deux champs d'une fiche Axonaut (`company.custom_fields`). Si plusieurs noms correspondent, la première
 * valeur non vide l'emporte.
 */
export function readAxonautAttribution(company: unknown): AxonautAttribution {
  const out: AxonautAttribution = { channel: null, apporteur: null };
  const customFields =
    company && typeof company === 'object' ? (company as Record<string, unknown>).custom_fields : null;
  for (const [name, value] of customFieldPairs(customFields)) {
    const kind = axonautFieldKind(name);
    if (!kind || out[kind] !== null) continue;
    out[kind] = fieldText(value);
  }
  return out;
}

// ------------------------------------------------------------------ Canaux

/** Identifiant stable en kebab-case ASCII : « Partenaire webdesign » → `partenaire-webdesign`. */
export function channelSlug(value: string): string {
  return normalizeAttributionText(value).replace(/ /g, '-');
}

/** Alias connus des canaux de l'onglet Suivi CA (clé du Cockpit). */
const CHANNEL_ALIASES: Record<string, string> = {
  'partenariat-webdesign': PARTNER_CHANNEL_KEY,
  'partenariats-webdesign': PARTNER_CHANNEL_KEY,
  'partenaire-webdesign': PARTNER_CHANNEL_KEY,
  'partenaire-web-design': PARTNER_CHANNEL_KEY,
  'partenaires-web-design': PARTNER_CHANNEL_KEY,
  'partenaire-webdesigner': PARTNER_CHANNEL_KEY,
  partenaires: PARTNER_CHANNEL_KEY,
  partenaire: PARTNER_CHANNEL_KEY,
  partenariat: PARTNER_CHANNEL_KEY,
  'reseau-pro': 'reseaux-pro',
  'reseaux-professionnels': 'reseaux-pro',
  'reseau-professionnel': 'reseaux-pro',
  'client-existant': 'clients-existants',
  recommandations: 'recommandation',
  'site-web': 'site-internet',
  autre: 'autres',
};
/** Clés connues, par forme tolérante au pluriel. */
const KNOWN_BY_LOOSE = new Map<string, string>(
  ACQUISITION_CHANNELS.map((c) => [looseKey(c.key.replace(/-/g, ' ')), c.key]),
);

/** Clé de regroupement d'un canal (slug du Cockpit ou libellé tapé dans Axonaut), alias ramenés aux clés connues. */
export function canonicalChannel(value: string): string {
  const slug = channelSlug(value);
  const alias = CHANNEL_ALIASES[slug];
  if (alias) return alias;
  return KNOWN_BY_LOOSE.get(looseKey(slug.replace(/-/g, ' '))) ?? slug;
}

/** Libellé de repli d'une clé : celui des canaux connus, sinon la clé lisible (« Bouche a oreille »). */
export function fallbackChannelLabel(key: string): string {
  const known = ACQUISITION_CHANNELS.find((c) => c.key === key);
  if (known) return known.label;
  const text = key.replace(/-/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Libellés tels que tapés dans Axonaut, par clé : le plus fréquent (à égalité, celui qui s'écrit comme le libellé
 * connu, puis le premier dans l'ordre alphabétique). Les clés sans valeur Axonaut prennent le libellé de repli.
 */
export function channelLabels(axonautValues: Iterable<string | null | undefined>): Map<string, string> {
  const counts = new Map<string, Map<string, number>>();
  for (const value of axonautValues) {
    if (!value) continue;
    const key = canonicalChannel(value);
    if (!key) continue;
    const byLabel = counts.get(key) ?? new Map<string, number>();
    byLabel.set(value, (byLabel.get(value) ?? 0) + 1);
    counts.set(key, byLabel);
  }
  const out = new Map<string, string>();
  for (const [key, byLabel] of counts) {
    const known = fallbackChannelLabel(key);
    const best = [...byLabel].sort(
      (a, b) =>
        b[1] - a[1] || Number(b[0] === known) - Number(a[0] === known) || a[0].localeCompare(b[0], 'fr'),
    )[0];
    if (best) out.set(key, best[0]);
  }
  return out;
}

export function channelLabelOf(key: string, labels?: ReadonlyMap<string, string>): string {
  return labels?.get(key) ?? fallbackChannelLabel(key);
}

export interface ChannelSources {
  /** Valeur Axonaut (champ « Canal d'acquisition »). */
  axonautAcquisitionChannel?: string | null;
  /** Saisie du Cockpit sur le client. */
  acquisitionChannel?: string | null;
}

export interface EffectiveChannel {
  key: string;
  /** Valeur Axonaut telle que tapée, sinon libellé de repli de la clé. */
  label: string;
  source: 'axonaut' | 'cockpit';
}

/**
 * Canal effectif : valeur Axonaut quand elle existe, sinon la saisie du Cockpit sur le client, puis sur ses
 * devis (le premier qui en a une). null : aucun canal.
 */
export function effectiveChannel(
  client: ChannelSources | null | undefined,
  quotes: readonly { acquisitionChannel: string | null }[] = [],
  labels?: ReadonlyMap<string, string>,
): EffectiveChannel | null {
  const axonaut = client?.axonautAcquisitionChannel?.trim();
  if (axonaut) {
    const key = canonicalChannel(axonaut);
    if (key) return { key, label: labels?.get(key) ?? axonaut, source: 'axonaut' };
  }
  const cockpit =
    client?.acquisitionChannel?.trim() ||
    quotes.find((q) => q.acquisitionChannel?.trim())?.acquisitionChannel;
  if (cockpit) {
    const key = canonicalChannel(cockpit);
    if (key) return { key, label: channelLabelOf(key, labels), source: 'cockpit' };
  }
  return null;
}

/** Canal « Partenaire webdesign » (ou un de ses alias). */
export const isPartnerChannelKey = (key: string | null | undefined): boolean =>
  !!key && canonicalChannel(key) === PARTNER_CHANNEL_KEY;

// ------------------------------------------------------------------ Apporteurs

/** Clé de rapprochement d'un nom d'apporteur (nom du Cockpit, alias ou valeur Axonaut). */
export function partnerNameKey(name: string): string {
  return normalizeAttributionText(name);
}

export interface PartnerNames {
  id: string;
  name: string;
  aliases: readonly string[];
}

/** Apporteur du Cockpit par clé de nom (nom puis alias ; une clé ambiguë garde le premier rencontré). */
export function partnerDirectory(partners: readonly PartnerNames[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const p of partners) {
    const key = partnerNameKey(p.name);
    if (key && !out.has(key)) out.set(key, p.id);
  }
  for (const p of partners) {
    for (const alias of p.aliases) {
      const key = partnerNameKey(alias);
      if (key && !out.has(key)) out.set(key, p.id);
    }
  }
  return out;
}

/** Apporteur du Cockpit qui porte ce nom (ou cet alias), sinon null. */
export function matchPartner(
  directory: ReadonlyMap<string, string>,
  name: string | null | undefined,
): string | null {
  if (!name) return null;
  const key = partnerNameKey(name);
  return key ? (directory.get(key) ?? null) : null;
}

// ------------------------------------------------------------------ Règle d'attribution unique (lot BN)

/** Libellés de « lié par » (détail d'un apporteur, guide). */
export const APPORTEUR_LINKED_BY_LABELS: Record<ApporteurLinkedBy, string> = {
  axonaut: 'fiche Axonaut',
  axonaut_alias: 'fiche Axonaut (autre nom)',
  cockpit_client: 'saisi dans le Cockpit (client)',
  cockpit_quote: 'saisi dans le Cockpit (devis)',
  r1: 'infos du R1',
};

/** Annuaire des apporteurs pour la règle unique : noms et autres noms, genre, noms Axonaut ignorés. */
export interface ApporteurDirectory {
  /** Clé de nom (nom ou autre nom) → apporteur ; une clé ambiguë garde le premier rencontré (comme `partnerDirectory`). */
  byKey: ReadonlyMap<string, string>;
  /** Clés qui ne sont que des autres noms (rapprochement « par alias »). */
  aliasKeys: ReadonlySet<string>;
  /** Genre de chaque apporteur (absent = `person`). */
  kinds: ReadonlyMap<string, PartnerKind>;
  /** Noms Axonaut ignorés (« Ignorer ce nom ») : traités comme une fiche vide. */
  ignoredKeys: ReadonlySet<string>;
}

export function apporteurDirectory(
  partners: readonly (PartnerNames & { kind?: PartnerKind | null })[],
  ignoredKeys: Iterable<string> = [],
): ApporteurDirectory {
  const byKey = partnerDirectory(partners);
  const own = new Set(partners.map((p) => partnerNameKey(p.name)).filter(Boolean));
  const aliasKeys = new Set<string>();
  for (const p of partners) {
    for (const alias of p.aliases) {
      const key = partnerNameKey(alias);
      if (key && !own.has(key)) aliasKeys.add(key);
    }
  }
  return {
    byKey,
    aliasKeys,
    kinds: new Map(partners.map((p) => [p.id, p.kind === 'channel' ? 'channel' : 'person'])),
    ignoredKeys: new Set(ignoredKeys),
  };
}

const asDirectory = (directory: ReadonlyMap<string, string> | ApporteurDirectory): ApporteurDirectory =>
  directory instanceof Map
    ? { byKey: directory, aliasKeys: new Set(), kinds: new Map(), ignoredKeys: new Set() }
    : (directory as ApporteurDirectory);

export type ResolvedApporteur =
  | { kind: 'partner'; partnerId: string; linkedBy: ApporteurLinkedBy }
  /** Nom qu'aucun apporteur ne porte : fiche Axonaut (non ignoré), sinon infos du R1 ; gardé tel quel. */
  | { kind: 'unknown'; name: string; key: string; linkedBy: 'axonaut' | 'r1' };

export interface ApporteurResolution {
  /** Apporteur à remercier (`kind = 'person'`), sinon un nom inconnu, sinon null (« Sans apporteur »). */
  apporteur: ResolvedApporteur | null;
  /**
   * Apporteur `kind = 'channel'` rencontré avant l'apporteur retenu (ou sans apporteur) : section « Canaux
   * enregistrés comme apporteurs », et canal du client quand il n'en a aucun.
   */
  channelPartner: { partnerId: string; linkedBy: ApporteurLinkedBy } | null;
  /** Champ « Apporteur d'affaire » de la fiche : reconnu (`partnerId`), inconnu ou ignoré. */
  axonaut: {
    name: string;
    key: string;
    partnerId: string | null;
    viaAlias: boolean;
    ignored: boolean;
  } | null;
  /** Saisie du Cockpit : apporteur du client, sinon du premier devis qui en a un. */
  cockpitPartnerId: string | null;
  /** Désaccord : nom Axonaut reconnu différent de la saisie du Cockpit. */
  conflict: boolean;
}

/**
 * Lot BN : RÈGLE D'ATTRIBUTION UNIQUE de l'apporteur d'un client (Chiffres, Apporteurs, projets, fiches R1).
 * 1. le champ Axonaut « Apporteur d'affaire » quand son nom est reconnu (nom ou autre nom d'un apporteur) ;
 * 2. sinon l'apporteur saisi dans le Cockpit sur le client ;
 * 3. sinon celui du premier devis qui en a un (dans l'ordre donné) ;
 * 4. sinon l'apporteur saisi dans les infos du R1 (rapproché par le nom) ;
 * un nom Axonaut inconnu ou ignoré ne masque donc plus la saisie du Cockpit. Un apporteur `kind = 'channel'` est
 * passé (noté dans `channelPartner`) : la source suivante peut donner une personne. Sans personne : le nom Axonaut
 * inconnu (non ignoré), sinon le nom tapé dans les infos du R1, gardés tels quels ; sinon aucun apporteur.
 */
export function resolveApporteur(
  directory: ReadonlyMap<string, string> | ApporteurDirectory,
  client: { axonautApporteur?: string | null; partnerId?: string | null } | null | undefined,
  quotes: readonly { partnerId: string | null }[] = [],
  r1Referrer: string | null = null,
): ApporteurResolution {
  const dir = asDirectory(directory);
  const axonautName = client?.axonautApporteur?.trim() || null;
  const axonautKey = axonautName ? partnerNameKey(axonautName) : '';
  const axonautPartner = axonautKey ? (dir.byKey.get(axonautKey) ?? null) : null;
  const axonaut =
    axonautName && axonautKey
      ? {
          name: axonautName,
          key: axonautKey,
          partnerId: axonautPartner,
          viaAlias: !!axonautPartner && dir.aliasKeys.has(axonautKey),
          ignored: !axonautPartner && dir.ignoredKeys.has(axonautKey),
        }
      : null;
  const quotePartner = quotes.find((q) => q.partnerId)?.partnerId ?? null;
  const cockpitPartnerId = client?.partnerId ?? quotePartner;
  const referrer = r1Referrer?.trim() || null;
  const referrerKey = referrer ? partnerNameKey(referrer) : '';
  const r1Partner = referrerKey ? (dir.byKey.get(referrerKey) ?? null) : null;

  const candidates: { partnerId: string | null; linkedBy: ApporteurLinkedBy }[] = [
    { partnerId: axonautPartner, linkedBy: axonaut?.viaAlias ? 'axonaut_alias' : 'axonaut' },
    { partnerId: client?.partnerId ?? null, linkedBy: 'cockpit_client' },
    { partnerId: quotePartner, linkedBy: 'cockpit_quote' },
    { partnerId: r1Partner, linkedBy: 'r1' },
  ];
  let apporteur: ResolvedApporteur | null = null;
  let channelPartner: ApporteurResolution['channelPartner'] = null;
  for (const c of candidates) {
    if (!c.partnerId) continue;
    if (dir.kinds.get(c.partnerId) === 'channel') {
      channelPartner ??= { partnerId: c.partnerId, linkedBy: c.linkedBy };
      continue;
    }
    apporteur = { kind: 'partner', partnerId: c.partnerId, linkedBy: c.linkedBy };
    break;
  }
  if (!apporteur && axonaut && !axonaut.partnerId && !axonaut.ignored)
    apporteur = { kind: 'unknown', name: axonaut.name, key: axonaut.key, linkedBy: 'axonaut' };
  if (!apporteur && referrer && referrerKey && !r1Partner)
    apporteur = { kind: 'unknown', name: referrer, key: referrerKey, linkedBy: 'r1' };
  return {
    apporteur,
    channelPartner,
    axonaut,
    cockpitPartnerId,
    conflict: !!axonautPartner && !!cockpitPartnerId && axonautPartner !== cockpitPartnerId,
  };
}

export type EffectiveApporteur =
  | {
      kind: 'partner';
      partnerId: string;
      source: 'axonaut' | 'cockpit';
      axonautName: string | null;
      linkedBy: ApporteurLinkedBy;
    }
  | { kind: 'unknown'; name: string; key: string; source: 'axonaut' };

/**
 * Apporteur effectif d'un client, forme courte de `resolveApporteur` (règle unique du lot BN, sans infos du R1) :
 * nom Axonaut reconnu, sinon la saisie du Cockpit (client, puis ses devis), sinon le nom Axonaut inconnu tel quel.
 */
export function effectiveApporteur(
  directory: ReadonlyMap<string, string> | ApporteurDirectory,
  client: { axonautApporteur?: string | null; partnerId?: string | null } | null | undefined,
  quotes: readonly { partnerId: string | null }[] = [],
): EffectiveApporteur | null {
  const { apporteur, axonaut } = resolveApporteur(directory, client, quotes);
  if (!apporteur) return null;
  if (apporteur.kind === 'unknown') {
    return { kind: 'unknown', name: apporteur.name, key: apporteur.key, source: 'axonaut' };
  }
  const fromAxonaut = apporteur.linkedBy === 'axonaut' || apporteur.linkedBy === 'axonaut_alias';
  return {
    kind: 'partner',
    partnerId: apporteur.partnerId,
    source: fromAxonaut ? 'axonaut' : 'cockpit',
    axonautName: fromAxonaut ? (axonaut?.name ?? null) : null,
    linkedBy: apporteur.linkedBy,
  };
}

// ------------------------------------------------------------------ Audit des apporteurs (lot BN)

/** Mots vides (comparaison de noms) : articles, liaisons, formes juridiques. */
const STOP_WORDS = new Set([
  'a',
  'au',
  'aux',
  'chez',
  'd',
  'de',
  'des',
  'du',
  'en',
  'et',
  'l',
  'la',
  'le',
  'les',
  'par',
  'pour',
  'sur',
  'ta',
  'ton',
  'the',
  'and',
  'of',
  'sa',
  'sas',
  'sasu',
  'sarl',
  'eurl',
  'sci',
  'ei',
  'mme',
  'mr',
  'm',
]);
/**
 * Mots trop courants pour rapprocher deux noms (« Studio », « Com », « Partenariat », « Post Linkedin ») : ignorés
 * pour l'alias suspect et pour les noms inconnus qui ressemblent à un apporteur.
 */
const GENERIC_WORDS = new Set([
  'agence',
  'atelier',
  'studio',
  'com',
  'communication',
  'graphique',
  'graphiste',
  'design',
  'digital',
  'web',
  'creation',
  'partenariat',
  'partenaire',
  'post',
  'linkedin',
  'site',
  'internet',
  'salon',
  'reseau',
  'collegue',
  'conseil',
  'consulting',
  'groupe',
  'france',
  'vendee',
  'production',
  'assistante',
  'administrative',
]);

/** Singulier approché, « -eaux » compris (« réseaux » → « reseau »). */
const singularWord = (word: string) =>
  word.length > 4 && word.endsWith('eaux') ? word.slice(0, -1) : singular(word);

/** Mots d'un nom, sans accents ni casse, au singulier approché, mots vides retirés. */
export function nameWords(name: string): string[] {
  return normalizeAttributionText(name)
    .split(' ')
    .filter((w) => w && !STOP_WORDS.has(w))
    .map(singularWord);
}

/** Mots significatifs : sans les mots trop courants (s'il n'en reste aucun, tous les mots non vides). */
export function significantWords(name: string): string[] {
  const words = nameWords(name);
  const strong = words.filter((w) => !GENERIC_WORDS.has(w));
  return strong.length ? strong : words;
}

/** Distance d'édition (petits mots seulement). */
function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    for (let j = 1; j <= b.length; j++)
      cur[j] = Math.min(prev[j]! + 1, cur[j - 1]! + 1, prev[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1));
    prev = cur;
  }
  return prev[b.length]!;
}

/** Deux mots identiques, ou d'au moins 5 lettres à une faute près (« Idéolis » / « Idéolys »). */
const sameWord = (a: string, b: string) =>
  a === b || (a.length >= 5 && b.length >= 5 && editDistance(a, b) <= 1);

/** Un mot de 2 à 4 lettres égal aux initiales de mots qui se suivent dans l'autre nom (« CF » / « Carine Fournier »). */
function initialsOf(short: readonly string[], long: readonly string[]): boolean {
  return short.some(
    (t) =>
      t.length >= 2 &&
      t.length <= 4 &&
      long.some(
        (_, i) =>
          long.slice(i, i + t.length).length === t.length &&
          long
            .slice(i, i + t.length)
            .map((w) => w[0])
            .join('') === t,
      ),
  );
}

/**
 * Deux noms désignent-ils (probablement) la même personne ou société ? Un mot significatif en commun (à une faute
 * près), des initiales, ou l'un collé contenu dans l'autre (« Sowilostudio » / « SOWILO STUDIO »).
 */
export function namesLookAlike(a: string, b: string): boolean {
  const wa = significantWords(a);
  const wb = significantWords(b);
  if (wa.some((x) => wb.some((y) => sameWord(x, y)))) return true;
  if (initialsOf(wa, nameWords(b)) || initialsOf(wb, nameWords(a))) return true;
  const ca = normalizeAttributionText(a).replace(/ /g, '');
  const cb = normalizeAttributionText(b).replace(/ /g, '');
  const [short, long] = ca.length <= cb.length ? [ca, cb] : [cb, ca];
  return short.length >= 5 && long.includes(short);
}

/** Premiers mots d'un nom de société (« Domaine Asseray ») : pas un prénom. */
const BUSINESS_WORDS = new Set([
  'domaine',
  'chateau',
  'cave',
  'maison',
  'atelier',
  'studio',
  'agence',
  'cabinet',
  'garage',
  'societe',
  'entreprise',
  'ferme',
  'boutique',
  'restaurant',
  'hotel',
  'camping',
  'salon',
  'institut',
  'clinique',
  'pharmacie',
  'groupe',
  'imprimerie',
  'cafe',
  'association',
  'club',
  'credit',
  'caisse',
  'banque',
  'mairie',
  'ecole',
]);

/**
 * « Prénom Nom » contre « Prénom Nom » : même nom, prénoms différents (« Stéphane Magné » / « Françoise Magné »).
 * Une initiale, un diminutif contenu dans l'autre prénom (« Manu » / « Emmanuel ») ou un nom de société (« Domaine
 * Asseray ») ne comptent pas.
 */
function otherFirstName(a: string, b: string): boolean {
  const wa = nameWords(a);
  const wb = nameWords(b);
  if (wa.length !== 2 || wb.length !== 2) return false;
  const [fa, la] = wa as [string, string];
  const [fb, lb] = wb as [string, string];
  if (!sameWord(la, lb) || sameWord(fa, fb)) return false;
  if (BUSINESS_WORDS.has(fa) || BUSINESS_WORDS.has(fb)) return false;
  return !(fa.includes(fb) || fb.includes(fa));
}

/**
 * Alias suspect : aucun mot significatif en commun avec le nom de l'apporteur (ni initiales, ni nom collé), ni avec
 * un de ses autres noms qui, lui, ressemble au nom (« Laurence Denis (Océan Production) » passe par « Frédéric
 * Flamens - Océan Production ») ; ou même nom de famille avec un autre prénom. Renvoie la raison, sinon null.
 */
export function suspiciousAliasReason(
  partnerName: string,
  alias: string,
  otherAliases: readonly string[] = [],
): string | null {
  if (otherFirstName(partnerName, alias)) return 'même nom de famille, autre prénom';
  if (namesLookAlike(partnerName, alias)) return null;
  const trusted = otherAliases.filter(
    (o) =>
      partnerNameKey(o) !== partnerNameKey(alias) &&
      namesLookAlike(partnerName, o) &&
      !otherFirstName(partnerName, o),
  );
  if (trusted.some((o) => namesLookAlike(o, alias))) return null;
  return 'aucun mot en commun avec le nom';
}

/**
 * Mots typiques d'un canal tapé à la place d'un nom d'apporteur (singulier, sans accents) : « Site internet /
 * Réseaux sociaux », « Salon exposant », « Post Linkedin »… (« réseau » seul n'y est pas : « Réseau Vendée Vous »
 * est un réseau d'entreprises, donc un apporteur.)
 */
export const CHANNEL_NAME_WORDS: readonly string[] = [
  'site',
  'internet',
  'salon',
  'sociaux',
  'partenariat',
  'linkedin',
  'facebook',
  'instagram',
  'tiktok',
  'youtube',
  'google',
  'gmb',
  'seo',
  'demarchage',
  'prospection',
  'publicite',
  'pub',
  'flyer',
  'foire',
  'annuaire',
  'recommandation',
  'bouche',
  'oreille',
  'emailing',
  'mailing',
  'newsletter',
];
const CHANNEL_NAME_WORD_SET = new Set(CHANNEL_NAME_WORDS);

/** Canaux génériques de repli comparés aux noms d'apporteurs (pas « Crédit Agricole », une banque, ni « Autres »). */
const GENERIC_FALLBACK_CHANNELS = ACQUISITION_CHANNELS.filter(
  (c) => c.key !== 'credit-agricole' && c.key !== 'autres',
).map((c) => c.label);

/**
 * Apporteur qui ressemble à un canal : nom égal (au regroupement près) ou très proche d'un canal (`channels` :
 * libellés en usage, plus les canaux génériques de repli), ou qui contient un mot typique d'un canal. Renvoie la
 * raison, sinon null.
 */
export function channelLikeReason(name: string, channels: readonly string[] = []): string | null {
  const key = canonicalChannel(name);
  const loose = looseKey(name);
  for (const label of [...channels, ...GENERIC_FALLBACK_CHANNELS]) {
    if (!label.trim()) continue;
    if (canonicalChannel(label) === key) return `même nom que le canal « ${label} »`;
    const other = looseKey(label);
    if (loose.length >= 6 && other.length >= 6 && editDistance(loose, other) <= 2)
      return `presque le nom du canal « ${label} »`;
  }
  const word = nameWords(name).find((w) => CHANNEL_NAME_WORD_SET.has(w));
  return word ? `contient « ${word} », un mot de canal` : null;
}

/**
 * Mots d'un canal qui parle d'une personne (réseaux pro, recommandation client/ami, partenaires webdesign, banque…) :
 * un client venu par ce canal devrait avoir un apporteur (carte « À vérifier »).
 */
export const PERSON_CHANNEL_WORDS: readonly string[] = [
  'reseau',
  'recommandation',
  'recommande',
  'partenaire',
  'partenariat',
  'webdesign',
  'banque',
  'banquier',
  'bouche',
  'oreille',
  'parrainage',
  'parrain',
  'ami',
  'famille',
  'apporteur',
  'bni',
  'club',
  'collegue',
  'connaissance',
  'entourage',
  'prescripteur',
  'comptable',
];
const PERSON_CHANNEL_WORD_SET = new Set(PERSON_CHANNEL_WORDS);

/** Le canal (clé ou libellé) parle-t-il d'une personne ? (« Réseaux pro », « Recommandation client/ami »…) */
export function isPersonChannel(channel: string | null | undefined): boolean {
  if (!channel) return false;
  if (isPartnerChannelKey(channel)) return true;
  return nameWords(canonicalChannel(channel).replace(/-/g, ' ')).some((w) => PERSON_CHANNEL_WORD_SET.has(w));
}

// ------------------------------------------------------------------ R1 et couverture de l'agenda (lot AM [38])

const COVERAGE_MONTHS = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
];

/** « 13 août », avec l'année quand elle diffère de celle d'aujourd'hui (« 13 août 2026 »). */
function coverageDay(from: string, today: string): string {
  const [y, m, d] = from.split('-').map(Number) as [number, number, number];
  const base = `${d === 1 ? '1er' : d} ${COVERAGE_MONTHS[m - 1]}`;
  return today.slice(0, 4) !== String(y) ? `${base} ${y}` : base;
}

/**
 * Lot AM [38] : précision de la colonne « R1 réalisés » quand l'agenda ne couvre pas toute la période
 * (« depuis le 13 août », « agenda non lu ») ; `from` = jour depuis lequel tous les agendas lus sont couverts.
 */
export function r1CoverageLabel(from: string | null, today: string): string {
  return from === null ? 'agenda non lu' : `depuis le ${coverageDay(from, today)}`;
}

/** Explication (infobulle, fenêtre de détail, guide) d'une période que l'agenda ne couvre pas entièrement. */
export function r1CoverageHint(from: string | null, today: string): string {
  return from === null
    ? 'aucun agenda n’est lu ; les R1 de l’historique du Sheet n’ont pas de client : pas de conversion'
    : `l’agenda n’est lu que depuis le ${coverageDay(from, today)} ; les R1 plus anciens (historique du Sheet) n’ont pas de client : pas de conversion`;
}
